import { createContext, useContext, useState, useEffect } from "react";

export type TowerRoleName =
  | "ABLE_TO_IP_BAN"
  | "ASSET_MANAGER"
  | "AUTHOR"
  | "BETA_TESTER"
  | "BYPASS_TWO_FACTOR_AUTHENTICATION"
  | "CAMPAIGNS_ADMIN"
  | "CHORUS_MANAGER"
  | "COMMUNITY_POST_AUTHOR"
  | "EDITOR"
  | "EDITORIAL_APPS_ADMIN"
  | "EMPLOYEE"
  | "EXTERNAL_API_CLIENT"
  | "EXTERNAL_DEVELOPER"
  | "FREELANCE"
  | "GROWTH_AND_ANALYTICS"
  | "HEADLESS_ADMIN"
  | "INTERNAL_MEMBER"
  | "MANAGER"
  | "MEMBER"
  | "MODERATOR"
  | "NETWORK_ADMIN"
  | "NETWORK_EDITOR"
  | "NETWORK_OPS"
  | "PTE_EDITOR"
  | "PUBLIC_AUDIENCE"
  | "SUPERGROUP_AUTHOR"
  | "TRUSTED"
  | "TRUSTED_AUTHOR"
  | "%future added value";

export type TowerStatus =
  | "ACTIVE"
  | "BANNED"
  | "DEACTIVATED"
  | "MESSAGED"
  | "PERMISSIONS_SUSPENDED"
  | "PROVISIONAL"
  | "WARNED"
  | "%future added value";

export type Viewer = {
  id: number;
  username: string;
  status: TowerStatus;
  communityMembershipStatus: TowerStatus | null;
  email: string | null;
  emailVerified: boolean;
  avatar: string;

  /**
   * When provided, should be used as the Coral authentication SSO token.
   */
  coral?: string;
  roles: TowerRoleName[];
};

/**
 * A selection of fields from a user's promotions in the promotions collection
 */
export interface UserPromotion {
  promotionId: string;
  network: string;
  status: "registered" | "active" | "expired";
}

/**
 * NOTE: @packages/duet-framework/src/api/middleware/identity.ts has a zod schema
 * that satisfies this interface. Any changes to this interface should be reflected
 * in the identityUserSchema zod schema as well.
 */
export interface IdentityUser {
  uid: string;
  email: string;
  emailVerified?: boolean;
  commentingName?: string;
  profileId?: string;
  providerData?: { providerId: string }[];
  entitlements: string[];
  isEntitled: boolean;
  zephrJwt?: string | null;
  coral?: string;
  cycloneToken?: string | null;
  notificationsToken?: string | null;
  avatarUrl?: string;
  createdAt?: string;
  promotions?: UserPromotion[];
  joinedCommunities?: { cycloneCommunityId: string }[];
}

export interface AuthContextType {
  authMethod: "chorus" | "identity" | null;
  userLoading: boolean;
  setUser: (user: IdentityUser | null) => void;
  viewer?: Viewer | null;
  user?: IdentityUser | null;
}

const AuthContext = createContext<AuthContextType>({
  authMethod: null,
  userLoading: true,
  setUser: () => {},
});

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  return context;
}

export function useUser(): IdentityUser | Viewer | null {
  const context = useContext(AuthContext);

  if (!context.user && !context.viewer) {
    return null;
  }

  return context.user || context.viewer || null;
}

export function useUserId(): number | string | null {
  const context = useContext(AuthContext);

  if (!context.user && !context.viewer) {
    return null;
  }

  return context.user?.uid || context.viewer?.id || null;
}

export function useViewer(): Viewer | null {
  const context = useContext(AuthContext);

  return context.viewer || null;
}

export function useIdentity(): IdentityUser | null {
  const context = useContext(AuthContext);

  return context.user || null;
}

export function useIsEntitled(): boolean {
  const [isEntitled, setIsEntitled] = useState(false);
  const context = useContext(AuthContext);

  useEffect(() => {
    if (context.userLoading) {
      return;
    }

    if (context.user?.isEntitled) {
      setIsEntitled(true);
    }
  }, [context.user?.isEntitled, context.userLoading]);

  return isEntitled;
}

export default AuthContext;
